import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../chat/api";
import { getFreshAccessToken } from "../chat/api";
export default function GenerateProposalModal({ isOpen, onClose }) {
  const [loading, setLoading] = useState(false);
  const [rfps, setRfps] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedRfp, setSelectedRfp] = useState(null); // { id, name, key }
  const [submitting, setSubmitting] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
const [progress, setProgress] = useState(null);
const wsRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get("/api/v1/rfp/documents/");
        const items = Array.isArray(res?.data?.results) ? res.data.results : [];
       setRfps(items);
      } catch (e) {
        setRfps([]);
        console.error("Failed to fetch RFPs:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, [isOpen]);

  // close dropdown on outside click
  useEffect(() => {
    if (!dropdownOpen) return;
    const onDocClick = (e) => {
      if (!dropdownRef.current) return;
      if (!dropdownRef.current.contains(e.target)) setDropdownOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [dropdownOpen]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rfps;
    return rfps.filter((r) => (r.name || "").toLowerCase().includes(q));
  }, [rfps, search]);

  const canGenerate = !!selectedRfp && !submitting;

  const handleChooseExisting = (item) => {
    setSelectedRfp({ id: item.id, name: item.name, key: item.key });
    setDropdownOpen(false);
  };

  const smartTruncateFilename = (filename, head = 12, tail = 5) => {
    if (!filename) return "";
    const dot = filename.lastIndexOf(".");
    if (dot > 0) {
      const base = filename.slice(0, dot);
      const ext = filename.slice(dot);
      if (base.length <= head + tail) return filename;
      return `${base.slice(0, head)}...${base.slice(-tail)}${ext}`;
    } else {
      if (filename.length <= head + tail + 3) return filename;
      return `${filename.slice(0, head)}...${filename.slice(-tail)}`;
    }
  };

const connectWebSocket = async (pdfKey) => {
  try {
    const token = await getFreshAccessToken();
    const ws = new WebSocket(
      `wss://stagingbackend.docusensa.com/ws/upload-status/?token=${token}`
    );

    ws.onopen = () => console.log("Proposal WS connected");

  ws.onmessage = (event) => {
  try {
    const data = JSON.parse(event.data);
    if (data.pdf_key !== pdfKey) return;

    if (data.metadata?.scope !== "rfp_extraction") return;

    if (data.type === "progress") {
      setProgress(data.progress);
    } else if (data.type === "completed") {
      setProgress(100);
      setTimeout(() => {
        setProgress(null);
        onClose?.();
        navigate(`/create-proposal-question/${pdfKey}`);
      }, 1200);
    }
  } catch (err) {
    console.error("Proposal WS parse error", err);
  }
};


    ws.onclose = () => console.log("Proposal WS closed");
    wsRef.current = ws;
  } catch (err) {
    console.error("Failed to connect proposal WS", err);
  }
};


const triggerGenerate = async () => {
  if (!canGenerate) return;
  setSubmitting(true);
  setProgress(0);

  try {
    const pdfKey = selectedRfp.key;

    // kick off extraction
    const res = await api.post(`/api/v1/rfp/documents/${pdfKey}/extract-questions/`);

    // ✅ If already completed, skip WebSocket
    if (res?.data?.status === "completed") {
      setProgress(100);
      setTimeout(() => {
        setProgress(null);
        onClose?.();
        navigate(`/create-proposal-question/${pdfKey}`);
      }, 800);
      return;
    }

    // Otherwise, connect socket for live progress
    await connectWebSocket(pdfKey);

  } catch (err) {
    console.error("Extract questions failed", err);
    setSubmitting(false);
    setProgress(null);
  }
};




  if (!isOpen) return null;

return (
  <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-[1.5px]">
    <div className="w-[401px] h-[328px] rounded-[14px] bg-white relative">
      {progress !== null ? (
        // ---------------- PROGRESS SCREEN ----------------
        <div className="flex flex-col items-center justify-center h-full w-full p-6">
          <h3 className="text-[18px] font-semibold text-[#18101C] mb-2">
            Analyzing RFP…
          </h3>
          <p className="text-[14px] text-[#52525B] mb-8 text-center">
            We are extracting RFP requirements based on the documents, please wait for 2-4 mins
          </p>

          {/* Circular Progress */}
          <div className="relative w-40 h-40">
            <svg className="w-full h-full transform -rotate-90">
              <circle
                cx="80"
                cy="80"
                r="70"
                stroke="#E5E7EB"
                strokeWidth="12"
                fill="transparent"
              />
              <circle
                cx="80"
                cy="80"
                r="70"
                stroke="url(#gradient)"
                strokeWidth="12"
                fill="transparent"
                strokeDasharray={2 * Math.PI * 70}
                strokeDashoffset={2 * Math.PI * 70 * (1 - progress / 100)}
                strokeLinecap="round"
                className="transition-all duration-500 ease-in-out"
              />
              <defs>
                <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#8B55DD" />
                  <stop offset="100%" stopColor="#BB42AE" />
                </linearGradient>
              </defs>
            </svg>

            {/* % Text */}
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-2xl font-bold text-[#8B55DD] animate-pulse">
                {progress}%
              </span>
            </div>
          </div>
        </div>
      ) : (
        // ---------------- EXISTING DESIGN ----------------
        <>
          {/* Header */}
          <div className="flex items-start justify-between">
            <div>
              <h3 className="mt-[12px] ml-[16px] text-[#18101C] font-outfit text-[16px] font-semibold">
                Generate Proposal from Your RFP
              </h3>
              <p className="ml-[16px] text-[14px] text-[#52525B] font-outfit font-medium">
                Choose an RFP to generate proposal questions.
              </p>
            </div>
            <button
              className="text-[#18101C] text-[22px] leading-none px-2 mt-[12px] mr-1"
              onClick={onClose}
            >
              ×
            </button>
          </div>

          {/* Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setDropdownOpen((v) => !v)}
              className="w-[90%] ml-[16px] mt-[16px] h-[48px] rounded-[10px] border border-[rgba(0,0,0,0.10)] px-4 text-left flex items-center justify-between"
            >
              <span
                className={`font-outfit ${
                  selectedRfp
                    ? "text-[#1A1A1A] text-[15px]"
                    : "text-[13px] text-black/30"
                }`}
              >
                {selectedRfp
                  ? smartTruncateFilename(selectedRfp.name, 12, 5)
                  : "Choose an existing RFP"}
              </span>
              <img
                src="./dropdown-icons.svg"
                alt=""
                className={`ml-3 w-5 h-5 transition-transform ${
                  dropdownOpen ? "rotate-180" : ""
                }`}
              />
            </button>

            {dropdownOpen && (
              <div className="absolute z-50 mt-1 w-[90%] h-[188px] rounded-[12px] bg-white border border-[rgba(0,0,0,0.10)] p-3 ml-[16px] shadow-lg">
                <div className="flex items-center gap-2 w-[338px] h-[36px] px-3 py-1 rounded-[36px] border border-[rgba(0,0,0,0.10)]">
                  <img src="/search.svg" alt="" className="w-5 h-5" />
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="search here"
                    className="w-[260px] outline-none border-none font-outfit text-[14px]"
                  />
                </div>
                <div className="mt-3 overflow-auto h-[120px] pr-1">
                  {loading ? (
                    <div className="px-3 py-2 text-[#6A6A6A]">Loading…</div>
                  ) : filtered.length === 0 ? (
                    <div className="px-3 py-2 text-[#6A6A6A]">No RFPs found</div>
                  ) : (
                    <div className="flex flex-col gap-2">
                      {filtered.map((item) => (
                        <button
                          type="button"
                          key={item.id}
                          onClick={() => handleChooseExisting(item)}
                          className={`flex items-center gap-3 px-3 py-1 rounded-[6px] border ${
                            selectedRfp?.id === item.id
                              ? "bg-[#FEF9F7]"
                              : "bg-white"
                          } hover:bg-[#FEF9F7]`}
                        >
                          <img src="/files-icon.svg" alt="" className="w-5 h-5" />
                          <span className="font-outfit text-[15px] text-[#1A1A1A]">
                            {item.name}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="absolute bottom-6 right-6 flex items-center gap-3 w-[90%]">
            <button
              onClick={onClose}
              className="px-[36px] py-[8px] rounded-[6px] border border-[rgba(0,0,0,0.10)] text-[#3F3F46] bg-white hover:bg-[#F5F5F5]"
            >
              Cancel
            </button>
            <button
              disabled={!canGenerate}
              onClick={triggerGenerate}
              className={`px-[16px] py-[8px] rounded-[6px] border border-[rgba(0,0,0,0.10)] font-outfit text-[14px] font-semibold ${
                canGenerate
                  ? "bg-[#8B55DD] text-white hover:bg-[#7A4CC4]"
                  : "bg-[#E7DFF8] text-white cursor-not-allowed"
              }`}
            >
              {submitting ? "Generating…" : "Generate Response"}
            </button>
          </div>
        </>
      )}
    </div>
  </div>
);

}
